
import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import { ARTIFACT_URL, LOADING_MESSAGES } from '../constants';
import { fetchLockedImageBase64, generateOlekPfp } from '../services/geminiService';
import { saveToGallery, getCurrentUsername } from '../services/firebase';
import { Download, Eraser, Fingerprint, Sparkles, Check, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PfpGenerator: React.FC = () => {
  const navigate = useNavigate();
  const [traits, setTraits] = useState('');
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [base64Source, setBase64Source] = useState<string | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const [currentUser, setCurrentUser] = useState('');

  useEffect(() => {
    setCurrentUser(getCurrentUsername());
    
    fetchLockedImageBase64(ARTIFACT_URL)
      .then(setBase64Source)
      .catch(err => {
        console.error(err);
        setError("passport photo lost.");
      });
  }, []);

  useEffect(() => {
    let interval: any;
    if (isLoading) {
      interval = setInterval(() => {
        setLoadingMsg(LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]);
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!traits.trim() || !base64Source) return;

    setIsLoading(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const result = await generateOlekPfp(traits, base64Source);
      setGeneratedImage(result);
      
      // Auto Save (Type is 'pfp', so it won't show in main vault)
      saveToGallery(result, traits, 'pfp');
      
    } catch (err: any) {
      setError(err.message || "printer jammed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between border-b-4 border-black pb-4">
        <div>
          <h1 className="text-4xl font-black lowercase">Fake ID</h1>
          <p className="text-gray-500 font-bold lowercase">new identity, same olek</p>
        </div>
        <div className="hidden md:block">
           <div className="w-16 h-16 bg-black text-white rounded-full border-4 border-gray-400 flex items-center justify-center font-bold text-xs text-center p-2 -rotate-6 shadow-hard-sm">
             <Fingerprint size={24} />
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        
        {/* Controls */}
        <div className="space-y-6">
           <div className="bg-gray-100 p-4 border-4 border-black shadow-hard-sm rotate-[1deg]">
             <div className="flex gap-4 items-center">
                <img src={ARTIFACT_URL} className="w-16 h-16 border-2 border-black bg-white object-cover" alt="ref" />
                <div className="leading-tight">
                  <p className="font-bold">Subject: Olek</p>
                  <p className="text-sm">Modification: Add Accessories Only</p>
                </div>
             </div>
           </div>

           <form onSubmit={handleGenerate} className="space-y-4">
             <div>
               <label className="block font-bold text-xl mb-2 lowercase">add attributes:</label>
               <textarea
                 value={traits}
                 onChange={(e) => setTraits(e.target.value)}
                 placeholder="e.g. laser eyes, mcdonalds hat, gold chain, clown makeup..."
                 className="w-full h-40 border-4 border-black p-4 font-comic text-xl outline-none focus:bg-gray-50 resize-none shadow-hard-sm"
               />
               <p className="text-right text-xs font-bold text-gray-400 mt-2">forging for: {currentUser}</p>
             </div>
             
             <Button 
               type="submit" 
               disabled={isLoading || !base64Source} 
               variant="black"
               className="w-full py-4 text-xl flex items-center justify-center gap-2"
             >
               {isLoading ? (
                 <span>{loadingMsg}</span>
               ) : (
                 <>
                   <Sparkles size={24} /> forge identity
                 </>
               )}
             </Button>
           </form>
        </div>

        {/* Canvas / Result */}
        <div className="relative min-h-[500px] h-auto bg-white border-4 border-black shadow-hard flex flex-col items-center justify-center p-4">
           
           {/* Tape Effect */}
           <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-32 h-8 bg-gray-200 border border-gray-300 opacity-80 rotate-[1deg] z-10"></div>

           {error && (
             <div className="text-center space-y-2 max-w-xs">
               <Eraser size={48} className="mx-auto text-alert" />
               <p className="font-bold text-alert">{error}</p>
             </div>
           )}

           {!isLoading && !error && !generatedImage && (
             <div className="text-center opacity-20 select-none">
               <div className="border-4 border-dashed border-black w-48 h-48 mx-auto mb-4 rounded-full flex items-center justify-center">
                 <Fingerprint size={64} />
               </div>
               <p className="text-xl font-bold">no id found</p>
             </div>
           )}

           {isLoading && (
             <div className="animate-spin text-6xl">
               🕵️
             </div>
           )}

           {generatedImage && (
             <div className="w-full h-full relative group flex flex-col gap-4">
               <img src={generatedImage} alt="Generated PFP" className="w-full h-auto object-contain" />
               
               <div className="flex items-center justify-center gap-2 bg-green-100 text-green-800 px-3 py-1 border-2 border-green-800 text-sm font-bold mx-auto w-fit rotate-[-2deg]">
                 <Check size={16} /> saved to vault automatically
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <a href={generatedImage} download={`olek_pfp_${Date.now()}.png`} className="w-full">
                   <Button variant="secondary" className="text-sm py-3 w-full flex items-center justify-center">
                     <Download size={18} className="inline mr-2" /> download
                   </Button>
                 </a>
                 <Button 
                   onClick={() => navigate('/forge')} 
                   variant="black"
                   className="text-sm py-3 w-full flex items-center justify-center"
                 >
                   view in vault <ArrowRight size={18} className="inline ml-2" />
                 </Button>
               </div>
             </div>
           )}
        </div>

      </div>
    </div>
  );
};

export default PfpGenerator;
